import React, { useEffect, useRef, useState } from 'react';
import DecimalMonetario from '../decimal';
import { TipoFormulario } from '../../services/constantes';
import { useNomeCaucao } from '../../hooks/useNomeCaucao';

interface Parcela {
    data: string;
    valor: number;
    editado: boolean;
    pago: boolean;
}

/**
 * Editor inline para parcelamento da caução. Gerencia estado interno (qtd, data inicio,
 * lista de parcelas) e notifica o pai via callback `onParcelasChange` toda vez que
 * a lista muda.
 *
 * Por que componente separado: o CustomFieldRenderer faz `Component = campo.component`
 * — se o componente for uma arrow function inline criada dentro do useMemo do pai, sua
 * referência muda a cada render e React desmonta/remonta os inputs (perdendo foco a cada
 * keystroke). Importando este componente como referência estável o problema some.
 *
 * Recebe metadados extras via `campo.extraProps` (cast em runtime — o tipo Campo padrão
 * não tem essa propriedade, então o pai injeta com `as any`).
 */
export interface ParcelasCaucaoEditorExtraProps {
    integradoAsaas: boolean;
    /** Forma de pagamento escolhida para a caução, como string do enum. '' = herda a do aluguel. */
    tipoDePagamentoCaucao?: string;
    onParcelasChange: (parcelas: { data: string; valor: number; pago: boolean }[]) => void;
    parcelasCaucaoRef?: React.MutableRefObject<{ data: string; valor: number; pago: boolean }[]>;
}

interface FieldRendererProps {
    value: any;
    onChange: (val: any) => void;
    campo: any;
    entidade?: any;
    handleInputChange?: (campo: string, valor: string | number | boolean) => void;
}

function calcularDataParcela(baseStr: string, index: number): string {
    const base = new Date(baseStr + 'T12:00:00');
    base.setMonth(base.getMonth() + index);
    return base.toISOString().split('T')[0];
}

const ParcelasCaucaoEditor: React.FC<FieldRendererProps> = ({ campo, entidade }) => {
    const nomeCaucao = useNomeCaucao();
    // Lê o valor da caução direto do entidade do form (já existe campo "valorCaucao").
    // Assim quando o usuário altera o valor lá em cima, o editor recalcula sozinho.
    const valorTotal = Number(entidade?.valorCaucao) || 0;
    const extra = (campo?.extraProps || {}) as ParcelasCaucaoEditorExtraProps;
    const integradoAsaas = !!extra.integradoAsaas;
    // '2' é CREDIT_CARD no enum BillingTypeAsaas. Comparação por string porque o valor vem do
    // select do formulário, que guarda tudo como texto.
    const vaiNoCartao = String(extra.tipoDePagamentoCaucao ?? '') === '2';
    const onParcelasChange = extra.onParcelasChange || (() => {});
    const parcelasCaucaoRef = extra.parcelasCaucaoRef;
    // Lê o ref no momento da montagem para restaurar estado anterior
    const [parcelasIniciais] = useState(() => parcelasCaucaoRef?.current ?? []);

    const [qtdParcelas, setQtdParcelas] = useState<number>(() => {
        if (parcelasIniciais.length > 0) return parcelasIniciais.length;
        return 2;
    });
    const [dataInicio, setDataInicio] = useState<string>(() => {
        if (parcelasIniciais.length > 0) return parcelasIniciais[0].data;
        if (entidade?.dataPagamento) {
            const dp = new Date(entidade.dataPagamento);
            if (!isNaN(dp.getTime())) return dp.toISOString().split('T')[0];
        }
        const d = new Date();
        d.setDate(d.getDate() + 30);
        return d.toISOString().split('T')[0];
    });
    const [parcelas, setParcelas] = useState<Parcela[]>(() => {
        if (parcelasIniciais.length > 0) {
            return parcelasIniciais.map(p => ({ ...p, editado: true }));
        }
        return [];
    });

    // Sincroniza com a data de primeiro pagamento do formulário quando o usuário muda
    useEffect(() => {
        if (entidade?.dataPagamento) {
            const dp = new Date(entidade.dataPagamento);
            if (!isNaN(dp.getTime())) {
                const formatted = dp.toISOString().split('T')[0];
                setDataInicio(prev => prev !== formatted ? formatted : prev);
            }
        }
    }, [entidade?.dataPagamento]);

    // Pula o primeiro recálculo quando restaurando parcelas anteriores
    const skipFirstRecalc = useRef(parcelasIniciais.length > 0);

    // Recalcula parcelas quando qtd / data / valorTotal mudam, preservando linhas editadas.
    useEffect(() => {
        if (skipFirstRecalc.current) {
            skipFirstRecalc.current = false;
            return;
        }
        if (qtdParcelas < 1 || valorTotal <= 0 || !dataInicio) {
            setParcelas([]);
            onParcelasChange([]);
            return;
        }
        const vBase = Math.round((valorTotal / qtdParcelas) * 100) / 100;
        setParcelas(prev => {
            const items: Parcela[] = [];
            for (let i = 0; i < qtdParcelas; i++) {
                const existing = prev[i];
                const valorDefault = i === qtdParcelas - 1
                    ? Math.round((valorTotal - vBase * (qtdParcelas - 1)) * 100) / 100
                    : vBase;
                items.push({
                    data: existing?.editado ? existing.data : calcularDataParcela(dataInicio, i),
                    valor: existing?.editado ? existing.valor : valorDefault,
                    editado: existing?.editado || false,
                    pago: existing?.pago || false,
                });
            }
            onParcelasChange(items.map(p => ({ data: p.data, valor: p.valor, pago: p.pago })));
            return items;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qtdParcelas, dataInicio, valorTotal]);

    const atualizarParcela = (idx: number, patch: Partial<Parcela>) => {
        setParcelas(prev => {
            const novo = prev.map((p, i) => i === idx ? { ...p, ...patch, editado: true } : p);
            onParcelasChange(novo.map(p => ({ data: p.data, valor: p.valor, pago: p.pago })));
            return novo;
        });
    };

    const soma = parcelas.reduce((acc, p) => acc + (p.valor || 0), 0);
    const diff = Math.round((soma - valorTotal) * 100) / 100;
    const formatReais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    // Espelha as regras que o backend aplica para decidir entre parcelamento de verdade e cobranças
    // separadas (PodeUsarParcelamentoNoAsaas). Serve só para avisar na hora; quem decide é o servidor.
    const uniformeParaParcelamento = (() => {
        if (parcelas.length <= 1) return false;
        if (parcelas.some(p => p.pago)) return false;
        if (parcelas.some(p => Math.abs((p.valor || 0) - (parcelas[0].valor || 0)) > 0.001)) return false;

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const primeira = new Date(parcelas[0].data + 'T12:00:00');
        if (primeira < hoje) return false;

        return parcelas.every((p, i) => p.data === calcularDataParcela(parcelas[0].data, i));
    })();

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginTop: 8, background: '#fafafa' }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555' }}>Qtd. parcelas</label>
                    <input
                        type="number"
                        min={1}
                        max={60}
                        value={qtdParcelas}
                        onChange={(e) => setQtdParcelas(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ width: 80, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555' }}>1º vencimento</label>
                    <input
                        type="date"
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4 }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555' }}>Valor total</label>
                    <div style={{ padding: '6px 8px', fontWeight: 700, color: '#1f467e' }}>{formatReais(valorTotal)}</div>
                </div>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: '#f3f4f6', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
                    <span style={{ width: 45, flexShrink: 0 }}>#</span>
                    <span style={{ flex: 1, minWidth: 0 }}>Vencimento</span>
                    <span style={{ width: 220, flexShrink: 0 }}>Valor</span>
                    <span style={{ width: 70, flexShrink: 0, textAlign: 'center' }}>Já pagou?</span>
                </div>
                {parcelas.map((p, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderTop: '1px solid #f3f4f6', background: p.pago ? '#f0fdf4' : p.editado ? '#fffbeb' : '#fff' }}>
                        <span style={{ width: 45, flexShrink: 0, fontSize: 12, fontWeight: 600, color: '#1f467e' }}>{idx + 1}/{parcelas.length}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <input
                                type="date"
                                value={p.data}
                                onChange={(e) => atualizarParcela(idx, { data: e.target.value })}
                                style={{ width: '100%', maxWidth: 170, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13 }}
                            />
                        </div>
                        <div style={{ width: 220, flexShrink: 0 }}>
                            <DecimalMonetario
                                campo={{ nome: `parcelaCaucao_${idx}`, tipo: 'monetario', required: true, modo: TipoFormulario.tpEdicao }}
                                getFieldValue={() => p.valor}
                                handleInputChange={(_n: string, v: number) => atualizarParcela(idx, { valor: v })}
                                largura="100%"
                            />
                        </div>
                        <div style={{ width: 70, flexShrink: 0, textAlign: 'center' }}>
                            <input
                                type="checkbox"
                                checked={p.pago}
                                onChange={(e) => atualizarParcela(idx, { pago: e.target.checked })}
                                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#1f467e' }}
                            />
                        </div>
                    </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: '#f0f4ff', borderTop: '2px solid #d6e0f0', fontWeight: 700 }}>
                    <span style={{ width: 45, flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, color: '#1f467e', fontSize: 13 }}>Soma</span>
                    <span style={{ width: 220, flexShrink: 0, color: Math.abs(diff) > 0.01 ? '#dc3545' : '#1f467e' }}>{formatReais(soma)}</span>
                    <span style={{ width: 70, flexShrink: 0 }} />
                </div>
            </div>
            {Math.abs(diff) > 0.01 && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 12 }}>
                    Atenção: a soma das parcelas difere do valor da {nomeCaucao.minusculo} em {formatReais(Math.abs(diff))}. Ajuste antes de salvar.
                </div>
            )}
            {integradoAsaas && !vaiNoCartao && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#e0f2fe', color: '#0c4a6e', borderRadius: 4, fontSize: 12 }}>
                    Cada parcela vai gerar uma cobrança individual no Asaas, com a descrição "{nomeCaucao.maiusculo} - &lt;placa&gt; N/M".
                </div>
            )}
            {integradoAsaas && vaiNoCartao && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: uniformeParaParcelamento ? '#dcfce7' : '#fef3c7', color: uniformeParaParcelamento ? '#166534' : '#92400e', borderRadius: 4, fontSize: 12 }}>
                    {uniformeParaParcelamento
                        ? `Vai virar um parcelamento no Asaas: o locatário passa o cartão uma vez e o valor é dividido em ${parcelas.length}x.`
                        : 'Para virar um parcelamento no cartão, as parcelas precisam ter o mesmo valor, vencer de mês em mês, nenhuma pode estar marcada como paga e a primeira não pode ser anterior a hoje. Do jeito que estão, o Asaas vai gerar uma cobrança de cartão separada por parcela.'}
                </div>
            )}
        </div>
    );
};

export default ParcelasCaucaoEditor;
